import React, { useEffect, useMemo, useRef } from "react";

import type {
    SliderButtonType,
    SliderValueOrderType,
} from "../../../../../../../../shared-logic/features/filters/utils/constants/filterConfigConstants";
import { useCommonLabel, type CommonLabelType } from "../../../../../../../../common-deprecated/utils/commonLabels";
import useButtonSlider from "../../../../../../../../shared-logic/features/filters/hooks/useButtonSlider";
import styles from "./ButtonSlider.module.scss";
import SanitizedRenderer from "../../../../../../../../common-deprecated/components/SanitizedRenderer";

export type ButtonSliderType = {
    order: SliderValueOrderType;
    minValue: number;
    maxValue: number;
    enableMinValue?: boolean;
    currentMinValue?: number;
    currentMaxValue: number;
    step: number;
    setValue: (value: number, type: SliderButtonType) => void;
    getValueLabel?: (max: number, min: number) => SanitizedString;
    setValueLabel?: (label: string) => void;
    getSecondaryValueLabel?: (max: number, min: number) => string; // Used for secondary price in dual currency
    setSecondaryValueLabel?: (label: string) => void; // Used for secondary price in dual currency
    filterName: string;
    // Aria overwrites should only be used for RTL support. Fallbacks already provided if not RTL
    ariaValues?: {
        ariaMinValue?: number;
        ariaMaxValue?: number;
        ariaMinLabelKey: CommonLabelType;
        ariaMaxLabelKey: CommonLabelType;
        ariaMinTitleLabelKey: CommonLabelType;
        ariaMaxTitleLabelKey: CommonLabelType;
    };
    showValueLabel?: boolean;
    // Callback to set the slider values in the parent component
    setSliderValues?: (min: number, max: number) => void;
};

/**
 * This is the common AEM Toyota Button slider used across the Car Filter and Model Filter.
 */
const ButtonSlider = (props: ButtonSliderType): JSX.Element => {
    const {
        enableMinValue,
        minValue,
        maxValue,
        step,
        order,
        currentMaxValue,
        currentMinValue,
        filterName,
        setSliderValues,
    } = props;

    const { ariaMinValue, ariaMaxValue, ariaMinLabelKey, ariaMaxLabelKey, ariaMinTitleLabelKey, ariaMaxTitleLabelKey } =
        props.ariaValues || {
            ariaMinValue: currentMinValue,
            ariaMaxValue: currentMaxValue,
            ariaMinLabelKey: "ariaSliderMinValue",
            ariaMaxLabelKey: "ariaSliderMaxValue",
            ariaMinTitleLabelKey: "ariaSliderMinValueTitleLabel",
            ariaMaxTitleLabelKey: "ariaSliderMaxValueTitleLabel",
        };

    const { setValue, getValueLabel, setValueLabel, getSecondaryValueLabel, setSecondaryValueLabel, showValueLabel } =
        props;

    const sliderBarRef = useRef<HTMLDivElement>(null);
    const { getValueFromXPos, getOnMouseDown, getOnTouchStart, getOnKeyDown, xPositionLeft, xPositionRight } =
        useButtonSlider(sliderBarRef, order, maxValue, minValue, step, setValue, currentMaxValue, currentMinValue, 24);

    const valueLabel =
        getValueLabel && !setValueLabel
            ? getValueLabel(getValueFromXPos(xPositionRight), enableMinValue ? getValueFromXPos(xPositionLeft) : 0)
            : "";

    const sliderMinValueLabel = useCommonLabel(ariaMinLabelKey, { value: ariaMinValue });
    const sliderMaxValueLabel = useCommonLabel(ariaMaxLabelKey, { value: ariaMaxValue });
    const sliderMinValueTitleLabel = useCommonLabel(ariaMinTitleLabelKey, { value: filterName });
    const sliderMaxValueTitleLabel = useCommonLabel(ariaMaxTitleLabelKey, { value: filterName });

    const xPositionLowest = Math.min(xPositionLeft, xPositionRight);
    const xPositionHighest = Math.max(xPositionLeft, xPositionRight);

    useEffect(() => {
        // Update value label
        if (setValueLabel && getValueLabel) {
            setValueLabel(
                getValueLabel(
                    getValueFromXPos(xPositionHighest),
                    enableMinValue ? getValueFromXPos(xPositionLowest) : 0,
                ),
            );
        }

        setSliderValues?.(enableMinValue ? getValueFromXPos(xPositionLowest) : 0, getValueFromXPos(xPositionHighest));

        // Update secondary value label (dual currency)
        if (setSecondaryValueLabel && getSecondaryValueLabel) {
            setSecondaryValueLabel(
                getSecondaryValueLabel(
                    getValueFromXPos(xPositionHighest),
                    enableMinValue ? getValueFromXPos(xPositionLowest) : 0,
                ),
            );
        }
    }, [currentMinValue, currentMaxValue, xPositionHighest, xPositionLowest, enableMinValue]);

    return useMemo(
        () => (
            <>
                {valueLabel && showValueLabel && (
                    <div className={styles.value}>
                        <SanitizedRenderer html={valueLabel} />
                    </div>
                )}
                <div className={styles.wrapper} ref={sliderBarRef}>
                    {enableMinValue && (
                        <button
                            className={styles.handle}
                            type="button"
                            style={{ left: `${xPositionLeft - 12}px` }}
                            onMouseDown={getOnMouseDown("min")}
                            onTouchStart={getOnTouchStart("min")}
                            onKeyDown={getOnKeyDown("min")}
                            title={sliderMinValueLabel}
                            role="slider"
                            aria-orientation="horizontal"
                            aria-valuenow={ariaMinValue}
                            aria-valuemin={minValue}
                            aria-valuemax={maxValue}
                            aria-label={sliderMinValueTitleLabel}
                        />
                    )}
                    <div
                        className={styles.track}
                        style={{
                            left: `${enableMinValue ? xPositionLowest : -12}px`,
                            width: `${xPositionHighest - xPositionLowest + (enableMinValue ? 0 : 12)}px`,
                        }}
                    />
                    <button
                        className={styles.handle}
                        type="button"
                        style={{ left: `${xPositionRight - 12}px` }}
                        onMouseDown={getOnMouseDown("max")}
                        onTouchStart={getOnTouchStart("max")}
                        onKeyDown={getOnKeyDown("max")}
                        title={sliderMaxValueLabel}
                        role="slider"
                        aria-orientation="horizontal"
                        aria-valuenow={ariaMaxValue}
                        aria-valuemin={minValue}
                        aria-valuemax={maxValue}
                        aria-label={sliderMaxValueTitleLabel}
                        tabIndex={0}
                    />
                </div>
            </>
        ),
        [enableMinValue, getOnMouseDown, getOnTouchStart, valueLabel, xPositionLeft, xPositionRight],
    );
};

export default ButtonSlider;
